import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import dayjs from 'dayjs'

import { jsPDF } from 'jspdf'
import { FieldTypes } from '../../utils/constants'
import useReservationsReportApi from '../../hooks/reservationsReport/useReservationsReportApi'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import useApartmentsApi from '../../hooks/apartments/useApartmentsApi'
import ListHeader from '../common/ListHeader'
import Form from '../common/Form'
import { DatePicker } from '../common/FormFields'
import useStyles from '../common/FormFields/FormFieldsStyle'

const showDevTools = process.env.NODE_ENV === 'development'

export default function ReportForm() {
  const classes = useStyles()
  const [values, setValues] = useState({
    buildings: [],
    apartment: null,
    from: dayjs().subtract(1, 'month'),
    to: dayjs(),
  })
  const [selectedBuildings, setSelectedBuildings] = useState([])

  const [{ reportState }, methods] = useReservationsReportApi()
  const [mockReport, setMockReport] = useState(null)
  const [{ buildingsState }, buildingMethods] = useBuildingsApi()
  const [{ getApartmentsFromBuildingState }, apartmentMethods] = useApartmentsApi()

  useEffect(() => {
    buildingMethods.getBuildings(true)
  }, [])

  useEffect(() => {
    if (selectedBuildings.length === 1) {
      apartmentMethods.getApartmentsFromBuilding(selectedBuildings[0].id)
    }
  }, [selectedBuildings])

  const buildParams = () => ({
    buildingIds: selectedBuildings.map((b) => b.id).join(','),
    apartmentId: values.apartment?.id,
    from: dayjs(values.from).startOf('day').toISOString(),
    to: dayjs(values.to).endOf('day').toISOString(),
  })

  const handleGenerate = () => {
    if (selectedBuildings.length === 0) return
    methods.generateReport(buildParams())
  }

  // DEV ONLY — remove before shipping
  const generateMockData = () => {
    const buildings = selectedBuildings.length > 0 ? selectedBuildings : [{ id: '1', name: 'Torre Norte' }, { id: '2', name: 'Edificio Sur' }]
    const apartments = ['101', '202', '303', '404', '505']
    const rows = buildings.flatMap((b) =>
      apartments.map((apt) => {
        const reservationCount = Math.floor(Math.random() * 10) + 1
        const roundPrices = [10, 15, 20, 25, 30, 50]
        const slotsCost = roundPrices[Math.floor(Math.random() * roundPrices.length)]
        const extraCost = Math.floor(Math.random() * 5) * 10
        return {
          buildingId: b.id,
          buildingName: b.name,
          apartmentId: apt,
          apartmentName: `Unidad ${apt}`,
          reservationCount,
          slotsCost,
          extraCost,
          totalCost: reservationCount * slotsCost + extraCost,
          currency: '$',
        }
      })
    )
    const totalCost = rows.reduce((s, r) => s + r.totalCost, 0)
    return {
      rows,
      totalsByCurrency: [{ currency: '$', reservationCount: rows.reduce((s, r) => s + r.reservationCount, 0), slotsCost: rows.reduce((s, r) => s + r.slotsCost, 0), extraCost: rows.reduce((s, r) => s + r.extraCost, 0), totalCost }],
    }
  }

  const handleDownloadMock = () => {
    const data = generateMockData()
    setMockReport(data)
    downloadPdf(data)
  }

  const handleDownloadMockPdf = () => {
    const source = mockReport || report
    if (!source) return
    downloadPdf(source)
  }

  const downloadPdf = (source) => {
    const doc = new jsPDF({ orientation: 'landscape' })
    doc.setFontSize(16)
    doc.text('Reporte de Reservas', 14, 16)
    doc.setFontSize(10)
    doc.text(`Generado: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 23)

    const headers = ['Edificio', 'Unidad', 'Reservas', 'Costo base', 'Adicional', 'Total', 'Moneda']
    const colWidths = [60, 40, 30, 40, 40, 40, 25]
    let y = 32

    doc.setFontSize(9)
    let x = 14
    doc.setFillColor(48, 104, 249)
    colWidths.forEach((w) => { doc.rect(x, y, w, 7, 'F'); x += w })
    doc.setTextColor(255, 255, 255)
    x = 14
    headers.forEach((h, i) => { doc.text(h, x + 2, y + 5); x += colWidths[i] })
    y += 7

    doc.setTextColor(0, 0, 0)
    source.rows.forEach((r, idx) => {
      if (y > 190) { doc.addPage(); y = 14 }
      if (idx % 2 === 0) { doc.setFillColor(240, 240, 240); x = 14; colWidths.forEach((w) => { doc.rect(x, y, w, 7, 'F'); x += w }) }
      x = 14
      const cells = [r.buildingName, r.apartmentName || '—', String(r.reservationCount), r.slotsCost.toFixed(2), r.extraCost.toFixed(2), r.totalCost.toFixed(2), r.currency]
      cells.forEach((c, i) => { doc.text(c, x + 2, y + 5); x += colWidths[i] })
      y += 7
    })

    y += 10
    doc.setFontSize(12)
    doc.text('Totales por moneda', 14, y)
    y += 7
    doc.setFontSize(9)
    source.totalsByCurrency.forEach((t) => {
      doc.text(`${t.currency}  |  Reservas: ${t.reservationCount}  |  Costo base: ${t.slotsCost.toFixed(2)}  |  Adicional: ${t.extraCost.toFixed(2)}  |  Total: ${t.totalCost.toFixed(2)}`, 14, y)
      y += 6
    })

    doc.save(`reporte-reservas-${dayjs().format('YYYY-MM-DD')}.pdf`)
  }

  const handleDownload = () => {
    if (selectedBuildings.length === 0) return
    methods.downloadPdf(buildParams())
  }

  const fields = [
    {
      type: FieldTypes.Autocomplete,
      label: 'Edificios',
      hint: 'Seleccione uno o más edificios',
      key: 'buildings',
      options: buildingsState.buildings || [],
      multiple: true,
      freeSolo: false,
      withMargin: true,
      onChange: (value) => {
        setSelectedBuildings(value)
        // A unit only makes sense for the building it was picked from.
        setValues((v) => ({ ...v, buildings: value, apartment: null }))
      },
      errorMessage: 'Seleccione al menos un edificio',
    },
    {
      type: FieldTypes.Autocomplete,
      label: 'Unidad (opcional)',
      hint: selectedBuildings.length === 1 ? 'Filtrar por unidad' : 'Seleccione un edificio para filtrar por unidad',
      key: 'apartment',
      options: selectedBuildings.length === 1 ? (getApartmentsFromBuildingState.apartments || []) : [],
      multiple: false,
      freeSolo: false,
      withMargin: true,
      disabled: selectedBuildings.length !== 1,
      onChange: (value) => setValues((v) => ({ ...v, apartment: value })),
    },
    {
      type: FieldTypes.Custom,
      key: 'dates',
      render: () => (
        <Box display="flex" style={{ gap: 16, marginBottom: 16 }}>
          <Box flex={1}>
            <DatePicker
              field={{ label: 'Desde', hint: 'Fecha de inicio', key: 'from', fullWidth: true }}
              values={values}
              setValues={setValues}
              errors={{}}
            />
          </Box>
          <Box flex={1}>
            <DatePicker
              field={{ label: 'Hasta', hint: 'Fecha de fin', key: 'to', fullWidth: true }}
              values={values}
              setValues={setValues}
              errors={{}}
            />
          </Box>
        </Box>
      ),
    },
  ]

  const report = mockReport || reportState.report

  return (
    <>
      <ListHeader bigTitle="Reporte de reservas" canCreate={false} />

      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Form
          values={values}
          setValues={setValues}
          fields={fields}
          handleSubmit={handleGenerate}
          leftButtons={
            <Box display="flex" style={{ gap: 8 }}>
              <Button
                className={classes.secondaryButton}
                variant="outlined"
                onClick={handleDownload}
                disabled={selectedBuildings.length === 0}
              >
                Exportar PDF
              </Button>
              {showDevTools && (
                <Button variant="outlined" onClick={handleDownloadMock}>
                  [DEV] Mock datos
                </Button>
              )}
            </Box>
          }
        />
      </Paper>

      {reportState.isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {report && report.rows && (
        <Box mt={3}>
          {report.warnings &&
            report.warnings.map((w) => (
              <Typography key={w} color="error" variant="caption">
                {w}
              </Typography>
            ))}

          <Typography variant="h6">Por unidad</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Edificio</TableCell>
                <TableCell>Unidad</TableCell>
                <TableCell align="right">Reservas</TableCell>
                <TableCell align="right">Costo base</TableCell>
                <TableCell align="right">Adicional</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Moneda</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.rows.map((r, i) => (
                <TableRow key={`${r.buildingId}-${r.apartmentId}-${r.currency}-${i}`}>
                  <TableCell>{r.buildingName}</TableCell>
                  <TableCell>{r.apartmentName || '—'}</TableCell>
                  <TableCell align="right">{r.reservationCount}</TableCell>
                  <TableCell align="right">{r.slotsCost.toFixed(2)}</TableCell>
                  <TableCell align="right">{r.extraCost.toFixed(2)}</TableCell>
                  <TableCell align="right">{r.totalCost.toFixed(2)}</TableCell>
                  <TableCell>{r.currency}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box mt={2}>
            <Typography variant="h6">Totales por moneda</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Moneda</TableCell>
                  <TableCell align="right">Reservas</TableCell>
                  <TableCell align="right">Costo base</TableCell>
                  <TableCell align="right">Adicional</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.totalsByCurrency.map((t) => (
                  <TableRow key={t.currency}>
                    <TableCell>{t.currency}</TableCell>
                    <TableCell align="right">{t.reservationCount}</TableCell>
                    <TableCell align="right">{t.slotsCost.toFixed(2)}</TableCell>
                    <TableCell align="right">{t.extraCost.toFixed(2)}</TableCell>
                    <TableCell align="right">{t.totalCost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </>
  )
}
